import { Check, CheckCheck, Trash2 } from '../../../icons';
import { SimpleTooltip } from '../../../components/ui/tooltip';
import { Button } from '../../../components/ui/button';
import type { ApproveControl } from './approveControl';

const ICON_BUTTON_CLASS =
  'relative size-10 shrink-0 rounded-lg border-foreground! bg-transparent text-foreground transition-colors hover:bg-foreground hover:text-white disabled:opacity-60';
const DESTRUCTIVE_ICON_BUTTON_CLASS =
  'relative size-10 shrink-0 rounded-lg border-destructive! bg-transparent text-destructive transition-colors hover:bg-destructive hover:text-white disabled:opacity-60';
const PILL_CLASS =
  'inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-foreground! bg-transparent px-5 text-sm font-semibold text-foreground transition-colors hover:bg-foreground hover:text-white disabled:cursor-not-allowed disabled:opacity-60';

function Spinner() {
  return <span className="size-4 animate-spin rounded-full border-2 border-foreground/30 border-t-foreground" />;
}

function CountBadge({ count }: { count: number }) {
  return (
    <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-semibold text-white">
      {count}
    </span>
  );
}

function controlText(control: ApproveControl): string {
  return `${control.label} (${control.count})`;
}

function ariaLabelFor(control: ApproveControl): string {
  return `${control.label} ${control.count} creators`;
}

/** Admin: compact double-tick icon (space-constrained row). */
export function ApproveIconButton({ control }: { control: ApproveControl }) {
  const icon = control.pending ? <Spinner /> : <CheckCheck size={18} weight="bold" />;
  return (
    <SimpleTooltip content={controlText(control)}>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={control.onClick}
        disabled={control.pending}
        aria-label={ariaLabelFor(control)}
        className={ICON_BUTTON_CLASS}
      >
        {icon}
        {control.isSelection && <CountBadge count={control.count} />}
      </Button>
    </SimpleTooltip>
  );
}

/** Brand: full-text pill with a tick, left of the search. */
export function ApprovePillButton({ control }: { control: ApproveControl }) {
  const content = control.pending ? (
    <Spinner />
  ) : (
    <>
      {controlText(control)}
      <Check size={16} weight="bold" />
    </>
  );
  return (
    <button
      type="button"
      onClick={control.onClick}
      disabled={control.pending}
      aria-label={ariaLabelFor(control)}
      className={PILL_CLASS}
    >
      {content}
    </button>
  );
}

/** Admin: permanently delete the selected pending creators (same as the row's delete). */
export function DeleteSelectedIconButton({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <SimpleTooltip content={`Delete selected permanently (${count})`}>
      <Button
        variant="outline"
        size="icon"
        type="button"
        onClick={onClick}
        aria-label={`Delete selected ${count} creators`}
        className={DESTRUCTIVE_ICON_BUTTON_CLASS}
      >
        <Trash2 size={18} />
      </Button>
    </SimpleTooltip>
  );
}
